import AppService from 'App/legacy/AppService';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import { comparisonTypes } from 'Resources/RequestBuilder';
import type RequestBuilder from 'Resources/RequestBuilder';
import type Product from 'App/resources/Model/Product';

/**
 * What the order-row product picker searches. A catalog small enough to hold in memory is searched by
 * `productSearchMatcher`, a larger one by the request `addProductSearchFilters` builds - with
 * PRODUCT_SEARCH_EVERY_WORD on, both apply the same rule, so which one a customer gets never changes what they find.
 */
export type ProductSearchFields = {
	everyWord: boolean;
	articleNoActive: boolean;
	descriptionActive: boolean;
	searchableCustomFieldIds: number[];
};

export function getProductSearchFields(): ProductSearchFields {
	const productFields = AppService.getMetadata().standardFields.Product;
	return {
		everyWord: hasSoftDeployAccess('PRODUCT_SEARCH_EVERY_WORD'),
		articleNoActive: !!productFields.ArticleNo?.active,
		descriptionActive: !!productFields.Description?.active,
		searchableCustomFieldIds: AppService.getReadOnlyCustomFields('product')
			.filter(customField => customField.searchable === 1)
			.map(customField => customField.id)
	};
}

const searchWords = (term: string) => term.split(/\s+/).filter(Boolean);

/** Built once per search: the returned test runs for every product in the catalog on each keystroke. */
export function productSearchMatcher(term: string, fields: ProductSearchFields): (product: Product) => boolean {
	const words = (fields.everyWord ? searchWords(term) : [term]).map(word => word.toLowerCase());
	return product => {
		const texts = [
			product.name,
			fields.everyWord || fields.articleNoActive ? product.articleNo : null,
			fields.descriptionActive ? product.description : null,
			...(product.custom ?? []).flatMap(customField =>
				fields.searchableCustomFieldIds.includes(customField.fieldId) && typeof customField.value === 'string'
					? [customField.value]
					: []
			)
		].map(text => (text || '').toLowerCase());
		return words.every(word => texts.some(text => text.includes(word)));
	};
}

/** Every word must match one of the fields; adds one group per word to `rb`. */
export function addProductSearchFilters(rb: RequestBuilder, term: string, fields: ProductSearchFields): void {
	const words = fields.everyWord ? searchWords(term) : term.split(' ');
	const termGroup = rb.groupBuilder();
	for (const word of words) {
		const orBuilder = termGroup.orBuilder();
		orBuilder.next();
		orBuilder.addFilter({ field: 'name' }, comparisonTypes.Search, word);
		orBuilder.next();
		orBuilder.addFilter({ field: 'articleNo' }, comparisonTypes.Search, word);
		if (fields.everyWord && fields.descriptionActive) {
			orBuilder.next();
			orBuilder.addFilter({ field: 'description' }, comparisonTypes.Search, word);
		}
		if (fields.searchableCustomFieldIds.length) {
			orBuilder.next();
			const customFieldGroup = orBuilder.groupBuilder();
			customFieldGroup.addFilter(
				{ field: 'custom.fieldId' },
				comparisonTypes.Equals,
				fields.searchableCustomFieldIds
			);
			customFieldGroup.addFilter({ field: 'custom.value' }, comparisonTypes.Search, word);
			customFieldGroup.done();
		}
		orBuilder.done();
	}
	termGroup.done();
}
